import { gql } from "@apollo/client";
import { POByNumber } from "../../modules/entityModules/CPOImport/query";

/**
 * Checks open quantities for POs listed in CSV data
 * @param {Object} params
 * @param {Object} params.client - Apollo Client instance
 * @param {Array} params.csvData - Array of objects with {code, poNumber, quantity}
 * @returns {Promise<Array>} Array of results with PO details and open quantities
 */
export default async function checkPOOpenQuantities({ client, csvData }) {
  const results = [];

  for (const row of csvData) {
    try {
      const { code, poNumber, quantity } = row;
      const poNumberFormatted = `A6P-${poNumber}`;
      const itemNumber = "00010";
      const qty = parseFloat(quantity);

      if (!code || !poNumber || !quantity || isNaN(qty)) {
        results.push({
          code,
          poNumber: `${poNumberFormatted}-${itemNumber}`,
          quantity: qty,
          status: "error",
          error: "Invalid data: missing code, poNumber, or quantity",
        });
        continue;
      }

      // Query PO by number and itemNumber
      let nextToken = null;
      let foundPO = null;

      do {
        const { data } = await client.query({
          query: gql(POByNumber),
          variables: {
            number: poNumberFormatted,
            itemNumber: { eq: itemNumber },
            limit: 1000,
            nextToken,
          },
          fetchPolicy: "network-only",
        });

        const items = data?.POByNumber?.items || [];
        
        // Find PO with matching code
        foundPO = items.find(
          (po) => po?.materialEntityRelation?.code === code
        );

        if (foundPO) {
          break;
        }

        nextToken = data?.POByNumber?.nextToken;
      } while (nextToken && !foundPO);

      if (!foundPO) {
        results.push({
          code,
          poNumber: `${poNumberFormatted}-${itemNumber}`,
          quantity: qty,
          status: "not_found",
          openQty: null,
          originalQty: null,
        });
        continue;
      }

      // Get current PO state
      const getPO = /* GraphQL */ `
        query GetPO($id: ID!) {
          getPO(id: $id) {
            id
            openQty
            qty
            number
            itemNumber
            materialEntityRelation {
              id
              code
            }
            __typename
          }
        }
      `;

      const { data: poData } = await client.query({
        query: gql(getPO),
        variables: { id: foundPO.id },
        fetchPolicy: "network-only",
      });

      const currentPO = poData?.getPO;
      if (!currentPO) {
        results.push({
          code,
          poNumber: `${poNumberFormatted}-${itemNumber}`,
          quantity: qty,
          status: "error",
          error: "Could not fetch PO details",
        });
        continue;
      }

      const currentOpenQty = parseFloat(currentPO.openQty) || 0;
      const originalQty = parseFloat(currentPO.qty) || 0;
      const difference = originalQty - currentOpenQty;
      const needsReversal = difference >= qty - 0.01; // Check if the deleted quantity matches the difference

      results.push({
        code,
        poNumber: `${poNumberFormatted}-${itemNumber}`,
        quantity: qty,
        status: "found",
        openQty: currentOpenQty,
        originalQty: originalQty,
        difference: difference,
        needsReversal: needsReversal,
        alreadyReversed: currentOpenQty >= originalQty - 0.01,
      });
    } catch (error) {
      results.push({
        code: row.code,
        poNumber: `A6P-${row.poNumber}-00010`,
        quantity: parseFloat(row.quantity) || 0,
        status: "error",
        error: error.message,
      });
      console.error(`Error processing row:`, row, error);
    }
  }

  return results;
}
